/**
 * «📈 Рост жалоб по отделениям» — `#growthBlock` (ui-blocks.md §8).
 *
 * Три уровня раскрытия: отделение → категория внутри отделения → список дел.
 * Кнопка «Развернуть все» / «Свернуть все». Блок «за период».
 *
 * Внутри раскрытого отделения — второе (и последнее) разрешённое место
 * Recharts: динамика обращений по бакетам вместо CSS-спарклайна макета
 * (DECISIONS.md, 2026-09-07). Цвета столбцов — те же, что у `.spark`.
 *
 * Числа шторки: `count` / `prev_count` / категории берём из
 * `GET /api/dashboard/department/:id` (это настоящие итоги за период), а сводку
 * по делам («в работе», «просрочено», «ср. срок ответа») и список дел третьего
 * уровня — из `GET /api/dashboard/list?kpi=department`. Поле `cases` детали
 * содержит лишь 20 последних строк, и считать по нему то, что стоит рядом с
 * настоящим итогом, нельзя. Если и выборка списка урезана лимитом ручки,
 * над чипами появляется подпись, по скольким делам они посчитаны.
 */

import { Fragment, useState } from 'react';
import { categoryLabel } from '@/lib/categories';
import type { KeyboardEvent } from 'react';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { useCaseList, useDepartmentDetail } from './api';
import { Delta, DeltaSmall, PriorityBadge } from './Delta';
import {
    appealWord,
    barWidth,
    catDotStyle,
    dayWord,
    daysBetween,
    fmtDate,
    fmtShort,
    num1,
    HEAD_NOTE,
} from './format';
import type {
    CaseListItem,
    DashboardRange,
    GrowthBlock,
    GrowthRow,
    OpenCase,
    PeriodValue,
} from './types';
import { LoadingState } from '../ErrorState';

/** Ключ строки: у «Отделение не указано» идентификатора нет. */
function rowKey(row: { department_id: string | null }): string {
    return row.department_id ?? 'none';
}

/** Бейдж состояния дела в списке третьего уровня. */
function statusBadge(item: CaseListItem) {
    if (item.stage === 'sent') return <span className="badge closed">закрыто</span>;
    if (item.deadline_state === 'late') return <span className="badge late">просрочено</span>;
    return <span className="badge open">в работе</span>;
}

interface CaseListProps {
    cases: CaseListItem[];
    /** Настоящий счётчик категории с бэкенда: «и ещё N» считаем от него, а не от выборки. */
    total: number;
    openCase: OpenCase;
}

/** Третий уровень — `caseListHtml()` макета: сортировка по дате, первые 8. */
function DrawerCaseList({ cases, total, openCase }: CaseListProps) {
    if (cases.length === 0) {
        return (
            <div className="detail-empty">
                {total > 0
                    ? `Дела этой категории (${total}) не попали в загруженную выборку — сузьте период, чтобы увидеть их.`
                    : 'Обращений нет.'}
            </div>
        );
    }

    const sorted = [...cases].sort((a, b) => b.created_at.localeCompare(a.created_at));
    const shown = sorted.slice(0, 8);
    const rest = Math.max(total, sorted.length) - shown.length;

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>, id: string): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openCase(id);
    };

    return (
        <div className="case-list">
            {shown.map((item) => (
                <div
                    className="case-row"
                    data-case={item.case_id}
                    tabIndex={0}
                    role="button"
                    key={item.case_id}
                    onClick={() => openCase(item.case_id)}
                    onKeyDown={(event) => onKeyDown(event, item.case_id)}
                >
                    <span className="cid">{item.number}</span>
                    <PriorityBadge priority={item.priority} />
                    <span className="ctitle">{item.title}</span>
                    <span className="cdate">{fmtShort(item.created_at)}</span>
                    {statusBadge(item)}
                </div>
            ))}
            {rest > 0 && (
                <div className="case-more">
                    и ещё {rest} {appealWord(rest)} в этой категории
                </div>
            )}
        </div>
    );
}

interface DrawerProps {
    row: GrowthRow;
    range: DashboardRange;
    period: PeriodValue;
    openCase: OpenCase;
}

function DepartmentDrawer({ row, range, period, openCase }: DrawerProps) {
    const [openCats, setOpenCats] = useState<Record<string, boolean>>({});
    const detail = useDepartmentDetail(row.department_id, period);
    /* `GET /api/dashboard/department/:id` отдаёт только 20 последних дел, а `count` —
       настоящий итог за период: сводка по этим двадцати противоречила бы сама себе.
       Полный список дел отделения за период берём отдельной выборкой (до 200 строк),
       и когда даже её не хватает — честно подписываем, по скольким делам считали. */
    const list = useCaseList(
        'department',
        { department_id: row.department_id ?? 'none' },
        period,
    );

    if (detail.isLoading) return <LoadingState variant="inline" label="Загружаем отделение…" />;
    if (detail.isError || !detail.data) {
        return <div className="detail-empty">Не удалось загрузить данные отделения.</div>;
    }

    const data = detail.data;
    const cases = list.data?.items ?? [];
    const listTotal = list.data?.total ?? data.count;
    // Сводка достоверна только когда выборка не урезана лимитом ручки.
    const exact = !list.isLoading && !list.isError && cases.length >= listTotal;
    const categories = [...data.categories]
        .filter((c) => c.count > 0 || c.prev_count > 0)
        .sort((a, b) => (b.count - a.count) || (b.prev_count - a.prev_count));
    const maxCat = categories.reduce((m, c) => Math.max(m, c.count), 1);

    // Сводка по чипам считается из полного списка дел отделения за период.
    const openCnt = cases.filter((c) => c.stage !== 'sent').length;
    const overdue = cases.filter((c) => c.stage !== 'sent' && c.deadline_state === 'late').length;
    const p1 = cases.filter((c) => c.priority === 1).length;
    const closed = cases.filter((c) => c.closed_at !== null);
    const just = closed.filter((c) => c.justified === true).length;
    const repeated = cases.filter((c) => c.repeat_of !== null).length;
    const avg =
        closed.length > 0
            ? closed.reduce((sum, c) => sum + daysBetween(c.created_at, c.closed_at ?? c.created_at), 0) /
              closed.length
            : 0;

    const bucketNote =
        data.bucket_days > 1 ? `шаг ${data.bucket_days} ${dayWord(data.bucket_days)}` : 'по дням';

    const toggleCat = (category: string): void => {
        setOpenCats((prev) => ({ ...prev, [category]: !prev[category] }));
    };

    const onCatKeyDown = (event: KeyboardEvent<HTMLDivElement>, category: string): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        toggleCat(category);
    };

    return (
        <div className="detail-box">
            <div className="detail-title">
                <span>📊 Категории обращений — {data.name}</span>
                <span className="period-note">
                    {fmtDate(range.from)} — {fmtDate(range.to)}
                </span>
            </div>

            {data.series.length > 1 && (
                <>
                    <ResponsiveContainer width="100%" height={120}>
                        <BarChart accessibilityLayer={false} data={data.series} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                            <XAxis dataKey="label" hide />
                            <Tooltip
                                cursor={{ fill: '#eef3f9' }}
                                contentStyle={{
                                    borderRadius: 10,
                                    border: '1px solid #dce3ed',
                                    fontSize: 14,
                                }}
                            />
                            <Bar dataKey="count" name="Обращений" radius={[3, 3, 0, 0]}>
                                {data.series.map((point) => (
                                    <Cell
                                        key={point.idx}
                                        fill={
                                            point.count === 0
                                                ? '#e3eaf3'
                                                : point.hot
                                                    ? '#d3919a'
                                                    : '#a8c6e0'
                                        }
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="spark-legend">
                        <span>{fmtShort(range.from)}</span>
                        <span>{bucketNote}</span>
                        <span>{fmtShort(range.to)}</span>
                    </div>
                </>
            )}

            {categories.length === 0 ? (
                <div className="detail-empty">
                    За выбранный период обращений по этому отделению не зарегистрировано.
                </div>
            ) : (
                categories.map((cat) => {
                    const key = `${rowKey(row)}|${cat.category}`;
                    const isOpen = Boolean(openCats[cat.category]);
                    return (
                        <Fragment key={cat.category}>
                            <div
                                className="cat-row"
                                data-deptcat={key}
                                role="button"
                                tabIndex={0}
                                aria-expanded={isOpen}
                                onClick={() => toggleCat(cat.category)}
                                onKeyDown={(event) => onCatKeyDown(event, cat.category)}
                            >
                                <span className="cat-name">
                                    <span className="chev">▶</span>
                                    <span className="dot" style={catDotStyle(cat.color)} />
                                    <span className="txt">{categoryLabel(cat.category)}</span>
                                </span>
                                <span className="cat-bar">
                                    <i style={{ width: barWidth(cat.count, maxCat), background: cat.color }} />
                                </span>
                                <span className="cat-num">
                                    {cat.count}
                                    <DeltaSmall delta={cat.delta} />
                                </span>
                            </div>
                            <div
                                className={isOpen ? 'drawer open' : 'drawer'}
                                data-drawer-deptcat={key}
                            >
                                <div className="drawer-inner">
                                    {isOpen && (
                                        <DrawerCaseList
                                            cases={cases.filter(
                                                (c) => c.primary_category === cat.category,
                                            )}
                                            total={cat.count}
                                            openCase={openCase}
                                        />
                                    )}
                                </div>
                            </div>
                        </Fragment>
                    );
                })
            )}

            <div className="chips">
                <span className="chip">
                    Всего за период: <b>{data.count}</b> · было {data.prev_count}
                </span>

                {list.isLoading && <span className="chip">Считаем сводку по делам…</span>}
                {list.isError && (
                    <span className="chip warn">Сводку по делам отделения загрузить не удалось</span>
                )}

                {!list.isLoading && !list.isError && (
                    <>
                        {!exact && (
                            <span className="chip warn">
                                Сводка ниже — по последним <b>{cases.length}</b> делам из {listTotal}
                            </span>
                        )}
                        <span className="chip">
                            В работе: <b>{openCnt}</b>
                        </span>
                        {overdue > 0 ? (
                            <span className="chip bad">
                                Просрочено: <b>{overdue}</b>
                            </span>
                        ) : (
                            <span className="chip good">Просрочек нет</span>
                        )}
                        {p1 > 0 && (
                            <span className="chip bad">
                                Приоритет 1: <b>{p1}</b>
                            </span>
                        )}
                        {closed.length > 0 && (
                            <span className="chip">
                                Обоснованных: <b>{just}</b> из {closed.length}
                            </span>
                        )}
                        {closed.length > 0 && (
                            <span className="chip">
                                Ср. срок ответа: <b>{num1(avg)} дн</b>
                            </span>
                        )}
                        {repeated > 0 && (
                            <span className="chip warn">
                                Повторных: <b>{repeated}</b>
                            </span>
                        )}
                    </>
                )}

                {data.head_name && (
                    <span className="chip">
                        Руководитель: <b>{data.head_name}</b>
                    </span>
                )}
            </div>
        </div>
    );
}

interface Props {
    growth: GrowthBlock;
    range: DashboardRange;
    period: PeriodValue;
    openCase: OpenCase;
}

export function DepartmentGrowth({ growth, range, period, openCase }: Props) {
    const rows = growth.rows;
    // На старте раскрыто первое отделение — как в макете.
    const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
        rows.length > 0 ? { [rowKey(rows[0]!)]: true } : {},
    );

    const maxCount = rows.reduce((m, r) => Math.max(m, r.count), 1);
    const expandedCount = rows.filter((r) => expanded[rowKey(r)]).length;
    const allExpanded = rows.length > 0 && expandedCount === rows.length;

    const toggle = (key: string): void => {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleAll = (): void => {
        if (allExpanded) {
            setExpanded({});
            return;
        }
        const next: Record<string, boolean> = {};
        for (const row of rows) next[rowKey(row)] = true;
        setExpanded(next);
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>, key: string): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        toggle(key);
    };

    return (
        <div className="stat-block growth-block" id="growthBlock">
            <div className="block-header">
                <span>
                    📈 Рост жалоб по отделениям{' '}
                    <span id="growthPeriod" style={HEAD_NOTE}>
                        · {range.label}
                    </span>
                </span>
                <button type="button" className="link-btn" id="toggleAll" onClick={toggleAll}>
                    {allExpanded ? 'Свернуть все' : 'Развернуть все'}
                </button>
            </div>

            <div className="block-body" id="growthBody">
                {rows.map((row) => {
                    const key = rowKey(row);
                    const isOpen = Boolean(expanded[key]);
                    return (
                        <Fragment key={key}>
                            <div
                                className="exp-row"
                                data-dept={key}
                                role="button"
                                tabIndex={0}
                                aria-expanded={isOpen}
                                onClick={() => toggle(key)}
                                onKeyDown={(event) => onKeyDown(event, key)}
                            >
                                <span className="label">
                                    <span className="chev">▶</span>
                                    <span>{row.icon ?? '🏥'}</span>
                                    <span className="txt">{row.name}</span>
                                </span>
                                <span className="row-bar">
                                    <i style={{ width: barWidth(row.count, maxCount) }} />
                                </span>
                                <span className="count">{row.count}</span>
                                <span className="change">
                                    <Delta delta={row.delta} />{' '}
                                    <span className="detail">
                                        (было {row.prev_count} → стало {row.count})
                                    </span>
                                    {row.growth_pct !== null && (
                                        <>
                                            {' '}
                                            <span className={row.surge ? 'up' : 'detail'}>
                                                {row.growth_pct > 0 ? '+' : ''}
                                                {row.growth_pct} %
                                            </span>
                                        </>
                                    )}
                                </span>
                            </div>
                            <div className={isOpen ? 'drawer open' : 'drawer'} data-drawer-dept={key}>
                                <div className="drawer-inner">
                                    {isOpen && (
                                        <DepartmentDrawer
                                            row={row}
                                            range={range}
                                            period={period}
                                            openCase={openCase}
                                        />
                                    )}
                                </div>
                            </div>
                        </Fragment>
                    );
                })}
            </div>

            <div className="totals-row" id="growthTotals">
                <span>Итого по учреждению · {range.label}</span>
                <span>
                    было {growth.prev_total} → стало <b>{growth.total}</b>{' '}
                    <span className="change">
                        <Delta delta={growth.total - growth.prev_total} />
                    </span>
                </span>
            </div>
        </div>
    );
}
